import React, { useRef, useEffect } from 'react';
import {
  View,
  StyleSheet,
  Modal,
  Pressable,
  Animated,
  Platform,
  BackHandler,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Sidebar } from './Sidebar';
import { getRequestFormSidebarExpandedWidth } from '../constants/requestFormLayout';

const EXPANDED_SIDEBAR_WIDTH = getRequestFormSidebarExpandedWidth(
  Platform.OS === 'web',
);

interface SidebarOverlayProps {
  isVisible: boolean;
  onClose: () => void;
  activeItem?: string;
  onItemPress?: (item: string) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  navigate?: (path: string) => void;
  onStepChange?: (stepId: string) => void;
  showSummaryStep?: boolean;
  requestStage?: string;
  requestId?: string | null;
  usePermissionFiltering?: boolean;
  isITAdministrator?: boolean;
  /** When true, overlay is scoped to the content pane (below header + request toolbar). */
  embeddedInFrame?: boolean;
}

export const SidebarOverlay: React.FC<SidebarOverlayProps> = ({
  isVisible,
  onClose,
  activeItem,
  onItemPress,
  isCollapsed: _isCollapsed,
  onToggleCollapse,
  navigate,
  onStepChange,
  showSummaryStep,
  requestStage,
  requestId,
  usePermissionFiltering = true,
  isITAdministrator = false,
  embeddedInFrame = false,
}) => {
  const insets = useSafeAreaInsets();
  const [modalVisible, setModalVisible] = React.useState(false);
  const slideAnim = useRef(new Animated.Value(-EXPANDED_SIDEBAR_WIDTH)).current;
  const backdropAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (isVisible) {
      setModalVisible(true);
      Animated.parallel([
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: 300,
          useNativeDriver: false,
        }),
        Animated.timing(backdropAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: false,
        }),
      ]).start();
    } else if (modalVisible) {
      Animated.parallel([
        Animated.timing(slideAnim, {
          toValue: -EXPANDED_SIDEBAR_WIDTH,
          duration: 250,
          useNativeDriver: false,
        }),
        Animated.timing(backdropAnim, {
          toValue: 0,
          duration: 250,
          useNativeDriver: false,
        }),
      ]).start(() => {
        setModalVisible(false);
      });
    }
  }, [isVisible, slideAnim, backdropAnim, modalVisible]);

  useEffect(() => {
    if (
      !embeddedInFrame ||
      !isVisible ||
      Platform.OS !== 'android' ||
      !BackHandler?.addEventListener
    ) {
      return undefined;
    }

    const subscription = BackHandler.addEventListener(
      'hardwareBackPress',
      () => {
        onClose();
        return true;
      },
    );

    return () => subscription.remove();
  }, [embeddedInFrame, isVisible, onClose]);

  const handleStepChange = (stepId: string) => {
    if (onStepChange) {
      onStepChange(stepId);
    }
    onClose();
  };

  const handleBackdropPress = () => {
    onClose();
  };

  const overlayBody = (
    <View
      style={[styles.container, embeddedInFrame && styles.containerEmbedded]}
    >
      <Animated.View
        style={[
          styles.backdrop,
          embeddedInFrame && styles.backdropEmbedded,
          {
            opacity: backdropAnim,
          },
        ]}
      >
        <Pressable style={styles.backdropPress} onPress={handleBackdropPress} />
      </Animated.View>

      <Animated.View
        style={[
          styles.drawer,
          embeddedInFrame && styles.drawerEmbedded,
          {
            paddingTop: embeddedInFrame ? 0 : insets.top,
            transform: [{ translateX: slideAnim }],
          },
        ]}
      >
        <View style={styles.sidebarContainer}>
          <Sidebar
            activeItem={activeItem}
            onItemPress={onItemPress}
            isCollapsed={false}
            onToggleCollapse={onToggleCollapse}
            navigate={navigate}
            onStepChange={handleStepChange}
            showSummaryStep={showSummaryStep}
            usePermissionFiltering={usePermissionFiltering}
            requestStage={requestStage}
            requestId={requestId}
            isITAdministrator={isITAdministrator}
            onClose={onClose}
            overlayMode={true}
            embeddedInFrame={embeddedInFrame}
          />
        </View>
      </Animated.View>
    </View>
  );

  if (embeddedInFrame) {
    if (!modalVisible) {
      return null;
    }

    return (
      <View
        style={styles.embeddedHost}
        pointerEvents="box-none"
        testID="sidebar-overlay-embedded"
      >
        {overlayBody}
      </View>
    );
  }

  return (
    <Modal
      visible={modalVisible}
      transparent={true}
      animationType="none"
      onRequestClose={onClose}
    >
      {overlayBody}
    </Modal>
  );
};

const styles = StyleSheet.create({
  embeddedHost: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 20,
  },
  container: {
    flex: 1,
    position: 'relative',
  },
  containerEmbedded: {
    flex: 1,
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  backdropEmbedded: {
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
  },
  backdropPress: {
    flex: 1,
  },
  drawer: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    width: EXPANDED_SIDEBAR_WIDTH,
    backgroundColor: '#fff',
    ...Platform.select({
      web: {
        boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)',
      },
      default: {
        // iOS clips dashed borders / dropdowns at drawer edges without visible overflow.
        overflow: 'visible' as const,
        shadowColor: '#000',
        shadowOffset: {
          width: 2,
          height: 0,
        },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        elevation: 5,
      },
    }),
  },
  drawerEmbedded: {
    zIndex: 1,
  },
  sidebarContainer: {
    flex: 1,
    width: '100%',
    ...Platform.select({
      ios: {
        overflow: 'visible' as const,
      },
      default: {},
    }),
  },
});
